import { useEffect, useMemo } from "react";
import { type FLIPT_FLAGS, USER_SUBSCRIPTIONS } from "@/constants/constants";
import { useGetFliptBatchFlags } from "@/helpers/get-flipt-flag";
import type { IUser, SubscriptionType } from "@/interfaces/user";
import type { FliptFlagType } from "@/types";
import { API_KEYS_ROUTE, HOME_ROUTE } from "../../constants/routes";
import { useAuthStore } from "../../store/authStore";
import type { SubscriptionContextData, UserData } from "./main.types";

const extractSubscriptionFlags = (
	flagList: FliptFlagType,
	subscriptions: string[] = USER_SUBSCRIPTIONS,
): Record<string, boolean> => {
	if (!subscriptions || !Array.isArray(subscriptions)) {
		return {};
	}

	return subscriptions.reduce(
		(acc, key) => {
			if (flagList && key in flagList) {
				acc[key] = flagList[key as keyof typeof flagList] || false;
			}
			return acc;
		},
		{} as Record<string, boolean>,
	);
};

/**
 * Gets active subscriptions from flag list
 */
const getActiveSubscriptions = (
	filteredFlags: Record<string, boolean>,
): SubscriptionType[] => {
	try {
		return Object.entries(filteredFlags)
			.filter(([, isActive]) => isActive)
			.map(([key]) => key as SubscriptionType);
	} catch (_error) {
		return [];
	}
};

/**
 * Determines the default route based on subscriptions
 */
const determineDefaultRoute = (subscriptions: SubscriptionType[]): string => {
	const hasOnlyDeviceFingerprint =
		subscriptions.length === 1 && subscriptions[0] === "device-fingerprint";

	return hasOnlyDeviceFingerprint ? API_KEYS_ROUTE : HOME_ROUTE;
};

/**
 * Hook to manage feature flags and subscriptions
 */
export const useFeatureFlags = () => {
	const subscriptions = USER_SUBSCRIPTIONS || [];

	const { flagList, isLoading } = useGetFliptBatchFlags([
		...subscriptions,
		"enable-trigger-FP-sdk-on-action",
		"webhooks-integration",
		"API-Keys-UI-Changes",
		"ai-rule-builder",
		"financial-events-report",
		"fusion-strategy-enabled",
		"customer-risk-rules-update",
		"onhold-enabled",
		"financial-fraud-cards",
		"behavioral-biometrics-pages",
		"multi-language-support",
	] as (keyof typeof FLIPT_FLAGS)[]);

	const subscriptionFlags = useMemo(
		() => (flagList ? extractSubscriptionFlags(flagList) : {}),
		[flagList],
	);

	const activeSubscriptions = useMemo(
		() => getActiveSubscriptions(subscriptionFlags),
		[subscriptionFlags],
	);

	return {
		flagList,
		isLoading,
		subscriptions: activeSubscriptions,
		subscriptionFlags,
	};
};

/**
 * Hook to manage subscription-based routing
 */
export const useSubscriptionRouting = (
	subscriptions: SubscriptionType[],
): SubscriptionContextData => {
	const defaultRoute = useMemo(
		() => determineDefaultRoute(subscriptions),
		[subscriptions],
	);

	const hasDeviceFingerprintOnly = useMemo(
		() => defaultRoute === API_KEYS_ROUTE,
		[defaultRoute],
	);

	const hasFinancialRiskOnly = useMemo(
		() => subscriptions.length === 1 && subscriptions[0] === "financial-risk",
		[subscriptions],
	);

	return {
		subscriptions,
		hasDeviceFingerprintOnly,
		hasFinancialRiskOnly,
		defaultRoute,
		isLoading: subscriptions.length === 0,
	};
};

/**
 * Hook to initialize user data in auth store
 */
export const useUserInitialization = (
	userData: UserData,
	subscriptions: SubscriptionType[],
	flagList: FliptFlagType | undefined,
	isLoading: boolean,
) => {
	const { setUser } = useAuthStore();

	useEffect(() => {
		if (isLoading) return;

		const user: IUser = {
			id: userData.sub,
			email: userData.email,
			first_name: userData.given_name,
			last_name: userData.family_name,
			role: userData.resource_access?.fraud?.roles?.[0] || [],
			email_verified: userData.email_verified,
			created_at: userData.created_timestamp,
			tenant: {
				id: parseInt(userData.tenant_id, 10),
				name: userData.tenant_name,
			},
			subscriptions,
			flags: flagList,
		};

		setUser(user);
	}, [
		isLoading,
		userData.sub,
		userData.email,
		userData.given_name,
		userData.family_name,
		userData.resource_access,
		userData.email_verified,
		userData.created_timestamp,
		userData.tenant_id,
		userData.tenant_name,
		subscriptions,
		flagList,
		setUser,
	]);
};

/**
 * Main hook that combines all subscription and user management logic
 */
export const useMainAppSetup = (userData: UserData) => {
	const { flagList, isLoading, subscriptions } = useFeatureFlags();
	const routingData = useSubscriptionRouting(subscriptions);

	useUserInitialization(userData, subscriptions, flagList, isLoading);

	return {
		...routingData,
		flagList,
		isLoading,
	};
};
